import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLanguage } from '../lib/language';
import { useTheme } from '../theme';

const textShadowLight = '0 1px 3px rgba(0,0,0,0.4), 0 2px 8px rgba(0,0,0,0.2)';
const getBgColor = (theme: { mood: string }, opacity: number = 0.4) => {
  const o = Math.max(0, Math.min(1, opacity * 0.82));
  return theme.mood === 'light' ? `rgba(255, 255, 255, ${o})` : `rgba(0, 0, 0, ${o})`;
};
const getBorderColor = (theme: { mood: string }, opacity: number = 0.1) =>
  theme.mood === 'light' ? `rgba(0, 0, 0, ${opacity})` : `rgba(255, 255, 255, ${opacity})`;
const CALENDAR_URL = 'https://calendar.google.com/calendar/appointments/schedules/AcZssZ1ZNd-9AnZBZepZsNeM32KTjfcbNb4Od_xsEUW1DyDGohjS-JDtaPfA0WRCAFlixFkWQHXpcexX?gv=true';

const Contact: React.FC = () => {
  const { language } = useLanguage();
  const { theme } = useTheme();
  const useDarkCalendar = theme.mood !== 'light';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="min-h-screen pt-28 sm:pt-32 pb-24">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24 items-start">
          {/* Left: Content */}
          <div className="space-y-12">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span
                className="text-[11px] uppercase tracking-[0.28em] mb-4 block"
                style={{ color: 'var(--theme-text-muted)', textShadow: textShadowLight }}
              >
                {language === 'es' ? 'Inicia un proyecto' : 'Start a project'}
              </span>
              <h1
                className="font-pixel text-4xl sm:text-5xl md:text-7xl font-black tracking-tight mb-6"
                style={{ color: 'var(--theme-text-primary)', textShadow: textShadowLight }}
              >
                {language === 'es' ? 'Contacto' : 'Contact'}
              </h1>
              <p
                className="text-lg max-w-md leading-relaxed"
                style={{ color: 'var(--theme-text-secondary)', opacity: 0.9 }}
              >
                {language === 'es'
                  ? 'Conversemos sobre tu proyecto. Definimos alcance, tiempos y una propuesta clara para construir con calidad y rapidez.'
                  : 'Let us discuss your project. We define scope, timeline, and a clear proposal to build with quality and speed.'}
              </p>
              <p className="mt-4 text-sm" style={{ color: 'var(--theme-text-muted)' }}>
                {language === 'es' ? 'También puedes ver el ' : 'You can also check the '}
                <Link to="/" className="underline hover:opacity-80">{language === 'es' ? 'inicio' : 'home page'}</Link>
                {language === 'es' ? ' o el ' : ' or '}
                <Link to="/studio" className="underline hover:opacity-80">{language === 'es' ? 'estudio' : 'studio'}</Link>.
              </p>
            </motion.div>

            <div
              className="rounded-2xl border p-6 sm:p-8 backdrop-blur-sm"
              style={{ backgroundColor: getBgColor(theme, 0.4), borderColor: getBorderColor(theme, 0.12) }}
            >
              <div className="space-y-0">
                <div className="py-6 border-b" style={{ borderColor: getBorderColor(theme, 0.12) }}>
                  <span className="text-[10px] uppercase tracking-[0.2em] mb-2 block" style={{ color: 'var(--theme-text-muted)' }}>
                    {language === 'es' ? 'Cobertura' : 'Reach'}
                  </span>
                  <p className="text-xl sm:text-2xl font-bold leading-snug" style={{ color: 'var(--theme-text-primary)' }}>
                    {language === 'es'
                      ? 'Desde cualquier parte del mundo, hacia cualquier parte del mundo.'
                      : 'From anywhere in the world, to anywhere in the world.'}
                  </p>
                  <p className="text-sm mt-2" style={{ color: 'var(--theme-text-muted)' }}>
                    {language === 'es' ? 'Software de calidad a escala global.' : 'Quality software at a global scale.'}
                  </p>
                </div>
                <div className="py-6 border-b" style={{ borderColor: getBorderColor(theme, 0.12) }}>
                  <span className="text-[10px] uppercase tracking-[0.2em] mb-2 block" style={{ color: 'var(--theme-text-muted)' }}>
                    {language === 'es' ? 'Disponibilidad' : 'Availability'}
                  </span>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full" style={{ boxShadow: '0 0 8px rgba(34, 197, 94, 0.5)' }} />
                    <p className="text-lg font-bold" style={{ color: 'var(--theme-text-primary)' }}>
                      {language === 'es' ? 'Aceptando nuevos proyectos' : 'Accepting new projects'}
                    </p>
                  </div>
                  <p className="text-sm mt-1" style={{ color: 'var(--theme-text-muted)' }}>
                    {language === 'es' ? 'Agenda una llamada esta semana' : 'Schedule a discovery call this week'}
                  </p>
                </div>
                <div className="py-6">
                  <span className="text-[10px] uppercase tracking-[0.2em] mb-3 block" style={{ color: 'var(--theme-text-muted)' }}>
                    {language === 'es' ? 'Redes' : 'Social'}
                  </span>
                  <div className="flex flex-wrap gap-4">
                    {[
                      { name: 'Twitter', url: '#' },
                      { name: 'LinkedIn', url: '#' },
                      { name: 'GitHub', url: 'https://github.com' },
                    ].map((social) => (
                      <a
                        key={social.name}
                        href={social.url}
                        className="group/link flex items-center gap-2 transition-colors hover:opacity-80"
                        style={{ color: 'var(--theme-text-secondary)' }}
                      >
                        <span className="text-sm font-medium">{social.name}</span>
                        <span className="material-symbols-outlined text-sm opacity-0 group-hover/link:opacity-100 -rotate-45 transition-all" style={{ color: 'var(--theme-text-accent)' }}>arrow_forward</span>
                      </a>
                    ))}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-3 pt-4 mt-4 border-t" style={{ borderColor: getBorderColor(theme, 0.12) }}>
                <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: 'var(--theme-text-muted)' }} />
                <span className="text-[11px] font-mono" style={{ color: 'var(--theme-text-muted)' }}>
                  {language === 'es' ? 'Servicios remotos · Disponibilidad global' : 'Remote services · Global availability'}
                </span>
              </div>
            </div>
          </div>

          {/* Right: Booking Calendar */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="relative"
          >
            <div
              className="relative overflow-hidden rounded-2xl border shadow-lg"
              style={{
                backgroundColor: getBgColor(theme, 0.5),
                borderColor: getBorderColor(theme, 0.15),
              }}
            >
              <div className="px-5 sm:px-6 py-4 border-b" style={{ borderColor: getBorderColor(theme, 0.12) }}>
                <p className="text-[10px] uppercase tracking-[0.24em]" style={{ color: 'var(--theme-text-muted)' }}>
                  {language === 'es' ? 'Agenda una reunión' : 'Schedule a meeting'}
                </p>
                <h2 className="font-pixel text-xl sm:text-2xl font-black mt-2" style={{ color: 'var(--theme-text-primary)' }}>
                  {language === 'es' ? 'Reserva una llamada' : 'Book a call'}
                </h2>
              </div>
              <div className="p-2 sm:p-3">
                <iframe
                  src={CALENDAR_URL}
                  style={{
                    border: 0,
                    backgroundColor: useDarkCalendar ? '#09090b' : '#ffffff',
                    filter: useDarkCalendar ? 'invert(1) hue-rotate(180deg) contrast(0.96) saturate(0.9)' : 'none',
                  }}
                  width="100%"
                  height="680"
                  frameBorder="0"
                  title="Calendario de reuniones"
                />
              </div>
            </div>
            <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
              <p className="text-[11px]" style={{ color: 'var(--theme-text-muted)' }}>
                {language === 'es' ? 'Si no carga el calendario, abre el enlace directo.' : 'If the calendar does not load, open the direct link.'}
              </p>
              <a
                href={CALENDAR_URL}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[11px] font-semibold uppercase tracking-[0.12em]"
                style={{ backgroundColor: 'var(--theme-btn-primary-bg)', color: 'var(--theme-btn-primary-text)' }}
              >
                {language === 'es' ? 'Abrir agenda' : 'Open calendar'}
                <span className="material-symbols-outlined text-sm">open_in_new</span>
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </main>
  );
};

export default Contact;
